<template>
  <div class="pr-4">
    <!-- Loading -->
    <div v-if="loading" role="status" class="text-muted italic py-8">
      {{ $t("loading") }}
    </div>

    <!-- Empty -->
    <div
      v-else-if="series.length === 0"
      class="flex flex-col items-start gap-4 py-12"
    >
      <p class="text-muted">{{ $t("following.empty") }}</p>
      <router-link
        to="/explore"
        class="text-highlight uppercase tracking-widest text-sm hover:underline"
      >
        {{ $t("nav.explore") }}
      </router-link>
    </div>

    <template v-else>
      <h1 class="text-2xl uppercase font-semibold text-white">
        {{ $t("nav.following") }}
        <span class="text-highlight">· {{ series.length }}</span>
      </h1>

      <div
        class="mt-8 grid gap-x-4 gap-y-8 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"
      >
        <div v-for="s in series" :key="s.id" class="group w-full">
          <!-- Poster -->
          <div
            class="relative w-full aspect-[2/3] rounded-lg overflow-hidden bg-primary-500 border border-primary-400"
          >
            <img
              v-if="poster(s)"
              :src="poster(s)"
              class="h-full w-full object-cover"
              :alt="s.name"
            />

            <!-- Unfollow. Always visible on touch screens (no hover there),
                 revealed on hover/focus on pointer devices. -->
            <button
              type="button"
              class="absolute top-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary-700/80 text-highlight transition hover:bg-primary-700"
              :class="revealClass(s)"
              :disabled="removing === s.id"
              :aria-busy="removing === s.id ? 'true' : 'false'"
              :aria-label="`${$t('following.unfollow')} — ${s.name}`"
              :title="$t('following.unfollow')"
              @click.prevent="unfollow(s.id)"
            >
              <LoadingSymbol v-if="removing === s.id" size="1x" />
              <Font-awesome-icon v-else :icon="['fas', 'heart']" />
            </button>
          </div>

          <!-- Meta -->
          <div class="pt-3">
            <p
              v-if="label(s)"
              class="text-highlight uppercase text-xs tracking-wide mb-1"
            >
              <span class="font-bold">{{ label(s).month }}</span
              >{{ label(s).day }}
              <span class="font-light opacity-90">
                - {{ label(s).status }}</span
              >
            </p>
            <p class="text-white text-sm truncate">
              {{ s.name }}
              <span v-if="year(s)" class="text-muted">({{ year(s) }})</span>
            </p>

            <!-- Vote -->
            <SeriesVote :series="s" class="mt-2" />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { upcomingLabel, seriesYear, seriesImage } from "@/utils/series";
import SeriesVote from "@/components/series/SeriesVote.vue";
import LoadingSymbol from "@/components/common/LoadingSymbol.vue";

export default {
  name: "Following",
  components: { SeriesVote, LoadingSymbol },

  data() {
    return {
      loading: false,
      removing: null,
    };
  },

  computed: {
    series() {
      return Object.values(this.$store.getters.seriesList);
    },
  },

  async mounted() {
    if (this.series.length === 0) {
      this.loading = true;
      try {
        await this.$store.dispatch("fetchAll");
      } catch {
        // Interceptor handles 401; nothing else to surface here.
      } finally {
        this.loading = false;
      }
    }
  },

  methods: {
    /** Hover-to-reveal only where hover exists; touch screens always show it. */
    revealClass(s) {
      if (this.removing === s.id) return "opacity-100";
      return "opacity-100 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100";
    },
    poster(s) {
      return seriesImage(s, "poster");
    },
    label(s) {
      return upcomingLabel(s);
    },
    year(s) {
      return seriesYear(s);
    },
    unfollow(id) {
      this.removing = id;
      this.$store
        .dispatch("untrack", id)
        .catch(() => undefined)
        .finally(() => (this.removing = null));
    },
  },
};
</script>
